import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import './styles.scss';
import clsx from 'clsx';
import { useChatStore, useConfigStore, useContextStore } from '@/hooks';
import { getHasCompletedForm } from '@/utils/form-state';
import { Questionnaire } from '../Questionnaire';
import { ChatInput, ChatMessage, DelayedRender, TypingIndicator } from '../ui';
import {
  resolveStringOrPageContextFunction,
  scrollToBottom as scrollToBottomUtil,
} from '@/utils';
import {
  shouldShowWelcomeBack,
  getWelcomeBackMessage,
} from '@/utils/session-helpers';
import Components from './components';
import { ScrollToBottomButton } from '../ui/ScrollToBottomButton';

const ANIMATION_DELAY_IN_MS = 400;

/** Wraps a message and reports "seen" to Front when an agent message enters viewport (once per message). */
const MessageSeenTracker: React.FC<{
  messageId: string;
  sender: 'user' | 'agent' | 'system';
  onMessageSeen?: (frontMessageId: string) => void;
  children: React.ReactNode;
}> = ({ messageId, sender, onMessageSeen, children }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const reportedRef = useRef(false);
  const isIntersectingRef = useRef(false);

  useEffect(() => {
    if (sender !== 'agent' || !onMessageSeen || !wrapperRef.current) return;

    // Ignore system or qualification messages (which might have IDs starting with 'qualification-' or 'agent-')
    // We only want to track real agent messages from Front
    if (
      messageId.startsWith('qualification-') ||
      messageId.startsWith('agent-')
    )
      return;

    const tryReport = () => {
      if (reportedRef.current || !isIntersectingRef.current) return;
      // Guard against Safari reporting 'visible' for backgrounded tabs.
      // document.hasFocus() is the most reliable cross-browser signal: a
      // backgrounded tab cannot hold OS-level window focus.
      if (!document.hasFocus() || document.visibilityState !== 'visible') return;
      reportedRef.current = true;
      onMessageSeen(messageId);
    };

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        isIntersectingRef.current = entry?.isIntersecting ?? false;
        tryReport();
      },
      { threshold: 0.5 }
    );

    // When the user returns to the tab (focus or visibility restored),
    // retry reporting if the message is already in the viewport.
    const handleFocus = () => tryReport();
    const handleVisibilityChange = () => tryReport();

    observer.observe(wrapperRef.current);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      observer.disconnect();
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [messageId, sender, onMessageSeen]);

  return <div ref={wrapperRef}>{children}</div>;
};

interface Props {
  onSendMessage: (content: string) => void;
  onTyping: (typing: boolean) => void;
  onFormSubmit: (formData: Record<string, string>) => void;
  onFormInteraction?: () => void;
  onContinueToChat: () => void;
  onActivity?: () => void;
  onMessageSeen?: (frontMessageId: string) => void;
}

export const Chat: React.FC<Props> = ({
  onSendMessage,
  onTyping,
  onFormSubmit,
  onFormInteraction,
  onContinueToChat,
  onActivity,
  onMessageSeen,
}) => {
  const config = useConfigStore();
  const {
    features: {
      chat: { welcomeMessage },
    },
  } = config;
  const {
    sessionId,
    connectionStatus,
    messages,
    filledQuestionnaireData,
    lastWelcomeBackShown,
    welcomeBackMessageIndex,
    addMessages,
    setLastWelcomeBackShown,
    setWelcomeBackMessageIndex,
    isFormSubmitting,
    isTyping,
    lockedWelcomeMessage,
    lockedCustomContext,
  } = useChatStore();
  const { pageContext, customContext: contextStoreCustomContext } =
    useContextStore();

  // Derive hasCompletedForm from existing state (no longer stored)
  // User can chat if they have: session, filled form data, or are on a proposal page
  const hasCompletedForm = getHasCompletedForm(
    sessionId,
    filledQuestionnaireData,
    contextStoreCustomContext.proposalId as string | undefined
  );

  // Use locked context if form was submitted, otherwise use current context
  const customContext =
    lockedCustomContext && hasCompletedForm
      ? lockedCustomContext
      : contextStoreCustomContext;

  // State machine: determine if we should skip questionnaire
  // ProposalConversation state (has proposalId) = auto-skip questionnaire
  // NewInquiry state (no proposalId) = show questionnaire (unless config overrides)
  const isProposalConversation = !!customContext.proposalId;
  const shouldSkipQuestionnaire =
    config.features.chat.skipQuestionnaire || isProposalConversation;

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [newMessagesWhileScrolled, setNewMessagesWhileScrolled] = useState(0);
  const prevMessageCountRef = useRef(0);

  // Ensure messages are always sorted by timestamp for consistent display
  const sortedMessages = useMemo(() => {
    return [...messages].sort((a, b) => a.timestamp - b.timestamp);
  }, [messages]);

  // Track scroll position and auto-scroll when new messages arrive
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const nearBottom = scrollHeight - scrollTop - clientHeight < 100;
      isNearBottomRef.current = nearBottom;
      setIsNearBottom(nearBottom);

      if (nearBottom) {
        setNewMessagesWhileScrolled(0);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Track new messages and auto-scroll or increment counter
  useEffect(() => {
    const currentCount = sortedMessages.length;
    const prevCount = prevMessageCountRef.current;

    if (currentCount > prevCount && prevCount > 0) {
      const newMessages = sortedMessages.slice(prevCount);
      const newAgentMessages = newMessages.filter(m => m.sender === 'agent');

      if (isNearBottomRef.current) {
        scrollToBottom();
      } else if (newAgentMessages.length > 0) {
        setNewMessagesWhileScrolled(prev => prev + newAgentMessages.length);
      }
    } else if (isNearBottomRef.current) {
      scrollToBottom();
    }

    prevMessageCountRef.current = currentCount;
  }, [sortedMessages.length]);

  // Calculate if chat input should be disabled
  const { inputDisabled, inputDisabledMessage } = useMemo(() => {
    // Allow chatting if form was completed OR if we have a session (returning user)
    if (!hasCompletedForm && !sessionId) {
      return {
        inputDisabled: true,
        inputDisabledMessage: 'Complete the form to start chatting',
      };
    }

    if (connectionStatus !== 'connected') {
      return {
        inputDisabled: true,
        inputDisabledMessage: 'Connecting to support...',
      };
    }

    // Front conversation ID is now stored in Supabase and looked up by session
    // No need to check for it locally

    return {
      inputDisabled: false,
      inputDisabledMessage: undefined,
    };
  }, [sessionId, connectionStatus, hasCompletedForm]);

  // Simple state
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showWelcomeBackMessage, setShowWelcomeBackMessage] = useState(false);
  const [showTyping, setShowTyping] = useState(false);

  const scrollToBottom = useCallback(() => {
    scrollToBottomUtil(chatContainerRef, 0);
    setNewMessagesWhileScrolled(0);
  }, []);

  // Helper to add agent messages with typing indicator
  const addAgentMessage = (content: string, sessionId: string = '') => {
    setShowTyping(true);

    setTimeout(() => {
      setShowTyping(false);
      addMessages([
        {
          id: `agent-${Date.now()}`,
          content,
          sender: 'agent' as const,
          timestamp: Date.now(),
          sessionId,
        },
      ]);
    }, ANIMATION_DELAY_IN_MS); // Reduced from 1500ms to 500ms for more natural typing effect
  };

  // Track if we've already shown the initial welcome message
  const [hasShownInitialWelcome, setHasShownInitialWelcome] = useState(false);

  useEffect(() => {
    const existingMessages = sortedMessages.length > 0;
    if (existingMessages) {
      // User has existing messages - show questionnaire if needed
      if (!shouldSkipQuestionnaire) {
        setShowQuestionnaire(true);
      }

      // Mark welcome as shown if we have existing messages (from history)
      if (shouldSkipQuestionnaire && !hasShownInitialWelcome) {
        setHasShownInitialWelcome(true);
      }

      // Show welcome back only for disconnected sessions (not resumed conversations)
      if (
        shouldShowWelcomeBack(sortedMessages, lastWelcomeBackShown) &&
        !showWelcomeBackMessage &&
        connectionStatus !== 'connected' &&
        hasCompletedForm
      ) {
        const name = filledQuestionnaireData?.name || undefined;
        const { message, nextIndex } = getWelcomeBackMessage(
          name,
          welcomeBackMessageIndex
        );

        addAgentMessage(message, 'welcome-back');
        setShowWelcomeBackMessage(true);
        setLastWelcomeBackShown(Date.now());
        setWelcomeBackMessageIndex(nextIndex);
      }
    } else if (
      !showTyping &&
      !hasCompletedForm &&
      !shouldSkipQuestionnaire &&
      !hasShownInitialWelcome
    ) {
      // New user without shouldSkipQuestionnaire - show welcome + questionnaire
      // Form hasn't been submitted yet, so use current config (no locked values)
      setHasShownInitialWelcome(true);
      setTimeout(() => {
        const welcomeMessageText = resolveStringOrPageContextFunction({
          value: welcomeMessage,
          pageContext,
          customContext,
        });

        addAgentMessage(welcomeMessageText || 'Welcome!', 'welcome');

        setTimeout(() => {
          setShowQuestionnaire(true);
          scrollToBottom();
        }, 500);
      }, 500);
    } else if (
      shouldSkipQuestionnaire &&
      hasCompletedForm &&
      !hasShownInitialWelcome &&
      !existingMessages
    ) {
      // Direct-to-chat mode - show welcome message ONLY for truly new sessions
      // Don't show if we're waiting for history to load
      // If form was submitted, use locked values; otherwise use current config
      setHasShownInitialWelcome(true);
      setTimeout(() => {
        // Use locked welcome message if form was submitted, otherwise resolve from config
        const welcomeMessageText = lockedWelcomeMessage
          ? lockedWelcomeMessage
          : resolveStringOrPageContextFunction({
              value: welcomeMessage,
              pageContext,
              customContext,
            });

        addAgentMessage(welcomeMessageText || 'Welcome!', 'welcome');
      }, 500);
    }
  }, [sortedMessages.length, shouldSkipQuestionnaire, hasCompletedForm]); // React to changes in message count, shouldSkipQuestionnaire, and form completion

  useEffect(() => {
    if (
      hasCompletedForm &&
      sessionId &&
      connectionStatus === 'disconnected' &&
      !isFormSubmitting
    ) {
      onContinueToChat();
    }
  }, [
    hasCompletedForm,
    sessionId,
    connectionStatus,
    isFormSubmitting,
    onContinueToChat,
  ]);

  // Handle form submission with typing indicator and confirm message
  const handleFormSubmit = (formData: Record<string, string>) => {
    // Call the original handler to save form data and establish connection
    // Connection will be automatically triggered via useEffect when form is completed
    // Success/error messages are now handled in the widget's handleFormSubmit
    onFormSubmit(formData);
  };

  const handleSendMessage = (content: string) => {
    onSendMessage(content);
    isNearBottomRef.current = true;
    scrollToBottom();
  };

  const initialMessage = sortedMessages.slice(0, 1)?.[0];
  const remainingMessages = sortedMessages.slice(1);

  return (
    <Components.Container
      containerRef={chatContainerRef}
      className={clsx('peptalk-chat-container', 'fade-in-up')}
      style={{ animationDelay: '150ms' }}
    >
      <div className={clsx('peptalk-chat')}>
        {initialMessage && (
          <MessageSeenTracker
            messageId={initialMessage.id}
            sender={initialMessage.sender}
            onMessageSeen={onMessageSeen}
          >
            <ChatMessage
              key={initialMessage.id}
              messages={[
                { id: initialMessage.id, message: initialMessage.content },
              ]}
              sender={initialMessage.sender}
              timestamp={initialMessage.timestamp}
              agentName={initialMessage.agentName}
              agentAvatarUrl={initialMessage.agentAvatarUrl}
              contentFormat={initialMessage.contentFormat}
            />
          </MessageSeenTracker>
        )}
        {showQuestionnaire && !shouldSkipQuestionnaire && (
          <DelayedRender
            delay={hasCompletedForm ? 0 : ANIMATION_DELAY_IN_MS}
            onRender={() => {
              if (hasCompletedForm) {
                scrollToBottom();
              }
            }}
            onRenderDelay={ANIMATION_DELAY_IN_MS}
          >
            <div className="fade-in-up">
              <Questionnaire
                disabled={hasCompletedForm}
                onSubmitQuestionnaire={handleFormSubmit}
                onFirstInteraction={onFormInteraction}
              />
            </div>
          </DelayedRender>
        )}

        {/* Display all messages directly */}
        {remainingMessages.length > 0 && (
          <DelayedRender
            delay={hasCompletedForm ? 0 : ANIMATION_DELAY_IN_MS}
            onRenderDelay={ANIMATION_DELAY_IN_MS}
            onRender={() => scrollToBottom()}
          >
            {remainingMessages.map(message => (
              <MessageSeenTracker
                key={message.id}
                messageId={message.id}
                sender={message.sender}
                onMessageSeen={onMessageSeen}
              >
                <div className="fade-in-up">
                  <ChatMessage
                    messages={[{ id: message.id, message: message.content }]}
                    sender={message.sender}
                    timestamp={message.timestamp}
                    agentName={message.agentName}
                    agentAvatarUrl={message.agentAvatarUrl}
                    contentFormat={message.contentFormat}
                  />
                </div>
              </MessageSeenTracker>
            ))}
          </DelayedRender>
        )}

        {/* Typing indicator - shown during form submission and agent typing */}
        {(showTyping || isTyping || isFormSubmitting) && (
          <div className="peptalk-typing-indicator-container fade-in-up">
            <TypingIndicator />
          </div>
        )}

        {!isNearBottom && newMessagesWhileScrolled > 0 && (
          <ScrollToBottomButton
            count={newMessagesWhileScrolled}
            onClick={scrollToBottom}
          />
        )}

        {/* Chat input - only show when socket is connected */}
        {hasCompletedForm && (
          <div className="peptalk-chat-input-container">
            <ChatInput
              onSendMessage={handleSendMessage}
              onTyping={onTyping}
              disabled={inputDisabled}
              disabledMessage={inputDisabledMessage}
              onFocus={onActivity}
            />
          </div>
        )}
      </div>
    </Components.Container>
  );
};
